import { useState } from "react";
import { useRecoilState } from "recoil";
import {
  NewReqFetalAntigenGridWrapper,
  OptionLabel,
} from "./NewRequisition.styles";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import type { FileUploadInfo } from "./NewReqFileUploader";
import {
  getFetalAntigenFieldSelection,
  hasAnyRbcAntigen,
  hasAnyPlateletAntigen,
} from "./fetalAntigenConstants";
import { FetalAntigenFields } from "./FetalAntigenFields";

type NewReqClinicalIndicationAneuploidyFetalAntigenProps = {
  active: boolean;
};

const NewReqClinicalIndicationAneuploidyFetalAntigen = ({
  active,
}: NewReqClinicalIndicationAneuploidyFetalAntigenProps) => {
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const { requisitionForm } = newRequisition;
  const [isFileUploadProcessing, setFileUploadProcessing] = useState(false);

  const { deviceType } = useDeviceHook();
  const { enabled: isFetalAntigenExpansion } = useBooleanFeatureFlag(
    FeatureFlags.FetalAntigenExpansion,
  );

  const handleRequireFetalAntigenFileChange = (
    requireFetalAntigenFile: boolean,
  ) => {
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        requireFetalAntigenFile,
      },
    }));
  };

  const handleAntigenFilesChange = (files: FileUploadInfo) => {
    setNewRequisition((current) => ({
      ...current,
      requisitionForm: {
        ...current.requisitionForm,
        antigenFileUrls: files,
      },
    }));
  };

  const isDisabled =
    !active ||
    !requisitionForm.aneuploidyNIPT ||
    requisitionForm.disableAneuploidy;

  const missingRbcAntigenSelection =
    newRequisition.isError &&
    active &&
    requisitionForm.isFetalAntigen &&
    requisitionForm.isFetalAntigenRbc &&
    !hasAnyRbcAntigen(requisitionForm);
  const missingPlateletAntigenSelection =
    newRequisition.isError &&
    active &&
    requisitionForm.isFetalAntigen &&
    requisitionForm.isFetalAntigenPlatelet &&
    !hasAnyPlateletAntigen(requisitionForm);
  const fileUploadLabelTone =
    requisitionForm.requireFetalAntigenFile &&
    !Object.keys(requisitionForm.antigenFileUrls).length &&
    newRequisition.isError
      ? "danger"
      : "dark";

  return (
    <>
      {requisitionForm.isFetalAntigenAccount && (
        <NewReqFetalAntigenGridWrapper
          deviceType={deviceType}
          disabled={isDisabled}
        >
          <FetalAntigenFields
            value={{
              isRbc: active && requisitionForm.isFetalAntigenRbc,
              isPlatelet: active && requisitionForm.isFetalAntigenPlatelet,
              selected: active
                ? getFetalAntigenFieldSelection(requisitionForm)
                : {},
            }}
            disabled={isDisabled}
            rhdIncluded={active && requisitionForm.aneuploidyIncludeRhd}
            expandedAntigensEnabled={isFetalAntigenExpansion}
            rbcError={missingRbcAntigenSelection}
            plateletError={missingPlateletAntigenSelection}
            nestedTopMargin
            onChange={(selection) =>
              setNewRequisition((current) => ({
                ...current,
                requisitionForm: {
                  ...current.requisitionForm,
                  isFetalAntigenRbc: selection.isRbc,
                  isFetalAntigenPlatelet: selection.isPlatelet,
                  ...(selection.selected as Partial<
                    typeof current.requisitionForm
                  >),
                },
              }))
            }
            requireFile={requisitionForm.requireFetalAntigenFile}
            allowPrintedDocument
            fileUrls={requisitionForm.antigenFileUrls}
            uploadProcessing={isFileUploadProcessing}
            preflight={newRequisition.preflight}
            documentPrompt={
              <OptionLabel $tone={fileUploadLabelTone}>
                <strong>
                  A lab report or consultation note confirming the
                  patient&apos;s alloimmunization*
                </strong>
              </OptionLabel>
            }
            printedOptionLabel={
              <OptionLabel $tone="muted">
                Attach a <strong> printed </strong> lab report or consultation
                note
              </OptionLabel>
            }
            printedMessage={
              <OptionLabel>
                Please <strong>print</strong> and <strong>attach </strong> a lab
                report or consultation note to confirm the patient&apos;s
                alloimmunization.
              </OptionLabel>
            }
            onRequireFileChange={handleRequireFetalAntigenFileChange}
            onFileUrlsChange={handleAntigenFilesChange}
            onUploadProcessingChange={setFileUploadProcessing}
          />
        </NewReqFetalAntigenGridWrapper>
      )}
    </>
  );
};
export default NewReqClinicalIndicationAneuploidyFetalAntigen;
